import { Button, Checkbox, FormControlLabel, TextField } from '@mui/material';
import { getValidator } from '@sprytki-frontend-apps/common';
import { LoginValues } from 'api/interfaces/login';
import { Controller, useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { getRegexTable } from 'shared';

import { LogoItem } from 'shared/components/LogoItem';
import { StyledComponents, StyledWrapper } from 'shared/components/StyledComponents';
import { StyledError } from './StyledComponents';
import { useLoginLogic } from './login.logic';

export const Login = () => {
	const methods = useForm<LoginValues>({
		mode: 'onChange',
		defaultValues: { password: '', rememberMe: false, userName: '' },
	});
	const {
		control,
		handleSubmit,
		formState: { isValid, errors },
	} = methods;
	const { t } = useTranslation();
	const regexTable = getRegexTable(t);
	const { loginError, sendRequest } = useLoginLogic();

	const formValidators = {
		email: getValidator(t, 50, regexTable.email, { value: true }, 1),
		password: getValidator(t, 50, null, { value: true }, 1),
		rememberMe: getValidator(t, null, null, { value: false }, 0),
	};

	return (
		<form onSubmit={handleSubmit(sendRequest)} id='login-form'>
			<StyledWrapper>
				<StyledComponents elevation={4}>
					<LogoItem id='login-logo-wrapper' />
					<Controller
						name='userName'
						control={control}
						rules={formValidators.email}
						render={({ field: { value, onChange } }) => (
							<TextField
								variant='standard'
								label={t('common.inputs.email')}
								autoComplete='username'
								inputProps={{
									inputMode: 'email',
									autoCapitalize: 'none',
									autoCorrect: 'off',
									spellCheck: false,
								}}
								defaultValue={value}
								onChange={onChange}
								id='login-input-userName'
								error={errors.userName !== undefined}
								helperText={errors.userName?.message}
							/>
						)}
					/>
					<Controller
						name='password'
						control={control}
						rules={formValidators.password}
						render={({ field: { value, onChange } }) => (
							<TextField
								variant='standard'
								name='password'
								label={t('common.inputs.password')}
								type='password'
								autoComplete='password'
								defaultValue={value}
								onChange={onChange}
								id='login-input-password'
								error={errors.password !== undefined}
								helperText={errors.password?.message}
							/>
						)}
					/>
					<div>
						<Controller
							name='rememberMe'
							control={control}
							defaultValue={false}
							render={({ field: { onChange } }) => (
								<FormControlLabel
									label={t('pages.login.rememberMe')}
									control={
										<Checkbox
											name='rememberMe'
											id='login-input-rememberMe'
											color='secondary'
											defaultChecked={false}
											onChange={onChange}
										/>
									}
								/>
							)}
						/>
						{loginError && <StyledError id='login-error'>{t('pages.login.loginError')}</StyledError>}
					</div>
					<div>
						<Button
							disabled={!isValid}
							variant='contained'
							type='submit'
							fullWidth
							id='login-from-submit-button'
						>
							{t('pages.login.login')}
						</Button>
					</div>
				</StyledComponents>
			</StyledWrapper>
		</form>
	);
};
